/**
 * Analytics Configuration - HIPAA-Safe Implementation
 * 
 * This module provides Google Analytics 4 and Google Tag Manager integration
 * with HIPAA-compliant data handling:
 * - No PHI in page paths (dashboard, order routes are anonymized)
 * - No user IDs or email addresses tracked
 * - IP anonymization enabled
 * - No custom dimensions with sensitive data
 */

// Google Analytics is set up in the document head (src/lib/analytics-bootstrap.ts,
// rendered by app/layout.tsx), which defines window.gtag for every browser it does
// not flag as automated. Tag Manager is optional and environment-based.
const GTM_CONTAINER_ID = process.env.NEXT_PUBLIC_GTM_CONTAINER_ID || '';

/** Events kept out of Google Analytics (PostHog still gets them). */
const GA4_SKIPPED_EVENTS = new Set(['web_vital']);

/** A crawler, headless or automation-driven browser, as flagged by the head script. */
export const isAutomatedBrowser = (): boolean =>
  typeof window !== 'undefined' && window.__suAutomated === true;

// Routes that should be anonymized (contain potential PHI or user-specific data)
const ANONYMIZED_ROUTES = [
  '/dashboard',
  '/patient-dashboard',
  '/pharmacy-dashboard',
  '/admin-dashboard',
  '/order/',
  '/patient-settings',
  '/pharmacy-settings',
  '/patient-profile',
  '/pharmacy-profile',
  '/notifications',
  '/upload-prescription',
];

// Anonymize sensitive routes for HIPAA compliance
export const anonymizePath = (path: string): string => {
  for (const route of ANONYMIZED_ROUTES) {
    if (path.startsWith(route)) {
      // Replace dynamic segments with generic placeholders
      if (route === '/order/') {
        return '/order/[redacted]';
      }
      return route;
    }
  }
  return path;
};

// Google Analytics is running in this browser (not a server render, not an automated browser).
export const isAnalyticsConfigured = (): boolean => {
  return typeof window !== 'undefined' && !isAutomatedBrowser() && typeof window.gtag === 'function';
};

// Initialize Google Tag Manager
export const initGTM = (): void => {
  if (!GTM_CONTAINER_ID || typeof window === 'undefined' || isAutomatedBrowser()) return;

  // GTM Data Layer
  window.dataLayer = window.dataLayer || [];
  
  // GTM Script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  
  // Push GTM initialization event
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });
  
  document.head.appendChild(script);
};

// Google Analytics 4 needs no initialization here: the head script defines gtag and
// queues the config, and app/layout.tsx loads gtag.js after the page. (initGA4 used
// to load gtag.js a second time behind NEXT_PUBLIC_GA_MEASUREMENT_ID, which was never
// set, and its gtag pushed arrays, which gtag.js ignores; removed 2026-10-25.)

// Page views for a Tag Manager container. Google Analytics counts page views
// itself (the page load, and each client-side navigation through enhanced
// measurement), so sending one here as well would count every page twice.
export const trackPageView = (path: string, title?: string): void => {
  if (!GTM_CONTAINER_ID || typeof window === 'undefined' || isAutomatedBrowser()) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'page_view',
    page_path: anonymizePath(path),
    page_title: title || document.title,
  });
};

// Track custom events (no PHI allowed)
// Sent to Google Analytics 4 (gtag), to Tag Manager when a container is configured,
// and to PostHog. Automated browsers send nothing.
// PostHog mirror added 2026-06-16 to give us session-replay + funnel keyed to the same event names.
export const trackEvent = (
  eventName: string,
  parameters?: Record<string, string | number | boolean>
): void => {
  if (typeof window === 'undefined' || isAutomatedBrowser()) return;

  // Sanitize parameters to prevent accidental PHI
  const sanitizedParams = parameters ? { ...parameters } : {};

  // Remove any potential PHI fields
  const phiFields = ['email', 'phone', 'name', 'address', 'user_id', 'patient_id', 'order_id', 'prescription_id'];
  phiFields.forEach(field => {
    delete sanitizedParams[field];
  });
  // No medicine, dose, search term or condition either (analyticsSafeParams).
  const safeParams = analyticsSafeParams(sanitizedParams);

  // Google Analytics 4. window.gtag comes from the head script, so an event sent
  // before gtag.js has loaded waits in the queue behind the config command. Until
  // 2026-10-25 this waited for NEXT_PUBLIC_GA_MEASUREMENT_ID, which was never set,
  // and no funnel event (request started, request sent, sign-up) reached GA4.
  if (typeof window.gtag === 'function' && !GA4_SKIPPED_EVENTS.has(eventName)) {
    try {
      window.gtag('event', eventName, safeParams);
    } catch {
      // Never let analytics break the page.
    }
  }

  // Tag Manager, only when a container is configured.
  if (GTM_CONTAINER_ID) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...safeParams,
    });
  }

  // PostHog mirror. posthog-js installed from npm never sets window.posthog,
  // so until 2026-10-16 this branch never ran and no funnel event reached
  // PostHog. PostHogProvider now sets window.posthog after init and replays
  // what was queued here before PostHog loaded (it loads when the browser is
  // idle, a few seconds after the page).
  try {
    const ph = window.posthog;
    if (ph && typeof ph.capture === 'function') {
      ph.capture(eventName, safeParams);
    } else {
      const queue = (window.__suPostHogQueue ||= []);
      if (queue.length < 50) queue.push([eventName, safeParams]);
    }
  } catch {
    // Never let analytics break the page.
  }
};

/**
 * What an analytics tool (Google Analytics, PostHog) may receive with an
 * event: no medicine, dose, search term or condition. A `source` naming the
 * page that sent the visitor (drug-metformin, compare-x-vs-y,
 * condition-asthma) keeps only its kind. The privacy policy says health data
 * never reaches analytics, and sharing which medicines a person looks up or
 * requests with an advertising company is what the FTC acted on against
 * GoodRx in 2023.
 */
const DROPPED_KEYS = new Set(['drug', 'drug_name', 'medication', 'medication_name', 'dosage', 'strength', 'q', 'query', 'search_term', 'condition', 'clinic']);
const SOURCE_WITH_TOPIC = /^(drug-class|drug|compare|condition|pet-condition|pet-medication|pet|class|guide)-./;
export function analyticsSafeParams(params: Record<string, string | number | boolean>): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (DROPPED_KEYS.has(key)) continue;
    if ((key === 'source' || key === 'src') && typeof value === 'string') {
      const m = value.match(SOURCE_WITH_TOPIC);
      out[key] = m ? m[1] : value;
      continue;
    }
    out[key] = value;
  }
  return out;
}

// Conversion tracking (e.g., signup, bid accepted)
export const trackConversion = (
  conversionType: 'signup_patient' | 'signup_pharmacy' | 'prescription_upload' | 'bid_accepted' | 'order_completed',
  value?: number
): void => {
  trackEvent(`conversion_${conversionType}`, {
    conversion_type: conversionType,
    ...(value !== undefined && { conversion_value: value }),
  });
};

// E-commerce tracking (anonymized)
export const trackPurchase = (
  transactionValue: number,
  currency: string = 'USD'
): void => {
  if (!isAnalyticsConfigured() || typeof window === 'undefined') return;
  
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      currency,
      value: transactionValue,
      // No transaction_id or item details to avoid PHI
    });
  }
};

// Initialize all analytics on app load (Tag Manager only; see the GA4 note above)
export const initAnalytics = (): void => {
  if (typeof window === 'undefined') return;

  // Use requestIdleCallback for non-blocking initialization
  const init = () => {
    initGTM();
  };
  
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(init, { timeout: 2000 });
  } else {
    setTimeout(init, 100);
  }
};

// Type declarations for global window object
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    /** Set by the head script (src/lib/analytics-bootstrap.ts): true for automated browsers. */
    __suAutomated?: boolean;
    /** Set by PostHogProvider once posthog-js has initialised. */
    posthog?: { capture?: (event: string, properties?: Record<string, unknown>) => void };
    /** Events captured before PostHog loaded; replayed by PostHogProvider. */
    __suPostHogQueue?: Array<[string, Record<string, unknown>]>;
  }
}

// ── Conversion tracking events (added Apr 18, 2026) ─────────────

/** Patient starts the bid flow */
export function trackBidFlowStart() {
  trackEvent('bid_flow_start', { event_category: 'conversion' });
}

/** Patient uploads a prescription */
export function trackPrescriptionUpload(method: 'camera' | 'file' | 'manual') {
  trackEvent('prescription_upload', { event_category: 'conversion', method });
}

/** Patient submits prescription for bidding */
export function trackPrescriptionSubmit() {
  trackEvent('prescription_submit', { event_category: 'conversion', value: 1 });
}

/** Patient accepts a bid (high-value conversion) */
export function trackBidAccepted(value: number) {
  trackEvent('bid_accepted', { event_category: 'conversion', value, currency: 'USD' });
}

/** Patient completes payment */
export function trackPaymentComplete(value: number) {
  trackEvent('purchase', { event_category: 'conversion', value, currency: 'USD', transaction_id: Date.now().toString(36) });
}

/** Pharmacy subscribes ($149/month) */
export function trackPharmacySubscription() {
  trackEvent('pharmacy_subscription', { event_category: 'b2b_conversion', value: 149, currency: 'USD' });
}

/** Pharmacy places a bid */
export function trackPharmacyBidPlaced(bidAmount: number) {
  trackEvent('pharmacy_bid_placed', { event_category: 'b2b_conversion', value: bidAmount, currency: 'USD' });
}

/**
 * User creates account. `method` is how: the email form, or a provider such as
 * google (fired from /auth/callback for a new account). Until 2026-10-25 every
 * sign-up was sent as method "google", the email form's included, and Google
 * sign-ups were not sent at all.
 */
export function trackSignup(userType: 'patient' | 'pharmacy', method: string = 'email') {
  // Credit the page (or outbound campaign) that sent this person, if we know it.
  let source = '';
  let campaign = '';
  try {
    source = localStorage.getItem(userType === 'pharmacy' ? 'pharmacyCtaSource' : 'pendingCtaSource') || '';
    // An outbound email that first brought a pharmacy in keeps the credit, even if a later click set `source`.
    if (userType === 'pharmacy') campaign = localStorage.getItem('pharmacyCampaign') || '';
  } catch {
    // storage unavailable
  }
  trackEvent('sign_up', { method, user_type: userType, source, ...(campaign ? { campaign } : {}) });
}
